import { getYear } from 'date-fns';

import {
  CreateEstateWaterfallInput,
  EntityGraphNodeConfigurationInput,
} from '@/types/schema';
import { assertNonNil } from '@/utils/assertUtils';

import { EstateWaterfallFormProps } from './EstateWaterfallForm';
import { EstateWaterfallFormData } from './types';

interface MapInputToFormData {
  householdId: string;
  grantors: EstateWaterfallFormProps['grantors'];
  displayName?: string;
  isHypothetical?: boolean;
  waterfallFilter?: EstateWaterfallFormData['waterfallFilter'];
  isDuplicate?: boolean;
  parentID?: string;
  shouldAutoGroup?: boolean;
}

export const mapInputToFormData = ({
  householdId,
  grantors,
  displayName = '',
  isHypothetical,
  waterfallFilter = null,
  parentID,
  shouldAutoGroup,
  ...projectionFields
}: MapInputToFormData): EstateWaterfallFormData => {
  const { id: firstGrantorDeathID } = assertNonNil(
    grantors[0],
    `Grantor is required to create a waterfall`
  );
  return {
    householdID: householdId,
    displayName,
    firstGrantorDeathID,
    _isHypothetical: isHypothetical ?? false,
    waterfallFilter,
    // Default to '' (not undefined) so the source-waterfall MUI Select mounts as
    // controlled and matches its empty option; undefined makes it uncontrolled.
    parentID: parentID ?? '',
    shouldAutoGroup: shouldAutoGroup ?? false,
    ...projectionFields,
  };
};

interface MapFormDataToInputInput {
  formData: EstateWaterfallFormData;
  isTwoGrantor: boolean;
  sourceEstateWaterfalls?: EstateWaterfallFormProps['sourceEstateWaterfalls'];
  householdId: string;
}

export const mapFormDataToInput = ({
  formData,
  isTwoGrantor,
  sourceEstateWaterfalls,
  householdId,
}: MapFormDataToInputInput): CreateEstateWaterfallInput => {
  const {
    _isHypothetical,
    waterfallFilter: _wF,
    shouldAutoGroup: _sAG,
    useCustomGlobalGrowthRate: _uCG,
    // Pulled out of the spread so the explicit assignment below is the only
    // place parentID lands in the returned input.
    parentID,
    ...formDataRest
  } = formData;

  const nodeConfigurations: EntityGraphNodeConfigurationInput[] | undefined =
    (() => {
      if (parentID) {
        const parent = sourceEstateWaterfalls?.find((ew) => ew.id === parentID);

        return parent?.nodeConfigurations || undefined;
      }

      return undefined;
    })();

  return {
    ...formDataRest,
    householdID: householdId,
    // parentID defaults to '' so the form Select stays controlled; never send an
    // empty parent reference to the backend. (Hypotheticals route through the
    // duplicate mutation, so the create path always has a falsy parentID.)
    parentID: parentID || undefined,
    // The user could manually clear these fields on the form, so we want to send these default values to the backend instead of empty strings
    firstGrantorDeathYear:
      formData.firstGrantorDeathYear || getYear(new Date()),
    secondGrantorDeathYear: isTwoGrantor
      ? formData.secondGrantorDeathYear || getYear(new Date())
      : undefined,
    nodeConfigurations,
  };
};
